import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { EhrDataListItem } from 'chaika-frontend-types/frontend-cms';

import { OptionType } from '@components/Select';
import { translate } from '@chaika/i18n/helper';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { DEFAULT_EHR_LIST_LIMIT, ehrQueryOptions, type EhrListFilters } from '@patient/queries/ehrQueries';

import { CREATE_PATIENT_VALUE, LabelFormat } from '../constants';
import { makePatientLabel } from '../utils/makePatientLabel';

interface Params {
    patientId?: string | null;
    executeId?: string;
    availableIds?: string[];
    filteredPatientIds?: string[];
    labelFormat?: LabelFormat[];
    fromLastViewedPatients?: boolean;
    defaultPatientIds?: string[];
    additionalOptions?: OptionType<any, { isEhrBlocked?: boolean }>[];
    onCreatePatientClick?(): void;
}

interface Data {
    defaultOptions: OptionType[];
    value: OptionType | undefined;
    selectedValue: string | null | undefined;
    loadOptions(query: string): Promise<OptionType[]>;
}

export function usePatientOptions({
    patientId,
    executeId,
    availableIds,
    filteredPatientIds,
    labelFormat = [LabelFormat.FullName],
    fromLastViewedPatients,
    defaultPatientIds,
    additionalOptions,
    onCreatePatientClick,
}: Params): Data {
    const { data: lastViewedPatients = [] } = useQuery({
        ...ehrQueryOptions.lastViewed(store.authUser.id),
        enabled: Boolean(fromLastViewedPatients),
    });

    const { data: patientsByIds = [] } = useQuery({
        ...ehrQueryOptions.list({ ids: defaultPatientIds ?? [] }),
        enabled: !fromLastViewedPatients && !!defaultPatientIds && defaultPatientIds.length > 0,
    });

    const { data: generalPatients = [] } = useQuery({
        ...ehrQueryOptions.list({ limit: DEFAULT_EHR_LIST_LIMIT }),
        enabled: !fromLastViewedPatients && defaultPatientIds === undefined,
    });

    const { data: selectedPatients = [] } = useQuery({
        ...ehrQueryOptions.list({ ids: patientId ? [patientId] : [] }),
        enabled: Boolean(patientId),
    });

    const debouncedFetch = useMemo(
        () =>
            AwesomeDebouncePromise(
                (filters: EhrListFilters) => queryClient.fetchQuery(ehrQueryOptions.list(filters)),
                300,
            ),
        [],
    );

    const patientOptions = makeDefaultOptions();
    const defaultOptions = filteredPatientIds
        ? patientOptions.filter(option => option.value && !filteredPatientIds.includes(option.value.toString()))
        : patientOptions;
    const value = filteredPatientIds ? patientOptions.find(option => option.value === patientId) : undefined;
    const selectedValue = value ? undefined : patientId;

    return { defaultOptions, value, selectedValue, loadOptions };

    function makeDefaultOptions(): OptionType[] {
        const patients = currentPatients();
        const options = patients.filter(patient => !executeId || patient.id !== executeId).map(toOption);

        const selectedPatient = selectedPatients[0];
        if (patientId && selectedPatient && !patients.some(patient => patient.id === patientId)) {
            options.unshift(toOption(selectedPatient));
        }

        additionalOptions?.forEach(({ isEhrBlocked = false, ...option }) => {
            options.unshift({ isEhrBlocked, ...option });
        });

        return withCreateOption(options);
    }

    async function loadOptions(query: string): Promise<OptionType[]> {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            return defaultOptions;
        }

        const patients = await debouncedFetch({ query: trimmedQuery, ids: availableIds });
        const options = patients
            .filter(patient => !filteredPatientIds?.some(id => id === patient.id))
            .filter(patient => !executeId || patient.id !== executeId)
            .map(toOption);

        return withCreateOption(options);
    }

    function currentPatients(): EhrDataListItem[] {
        if (fromLastViewedPatients) {
            return lastViewedPatients;
        }
        if (defaultPatientIds) {
            return patientsByIds;
        }
        return generalPatients;
    }

    function toOption(patient: EhrDataListItem): OptionType {
        return {
            value: patient.id,
            label: makePatientLabel(patient, labelFormat),
            isEhrBlocked: patient.isBlocked,
        };
    }

    function withCreateOption(options: OptionType[]): OptionType[] {
        return onCreatePatientClick
            ? [{ value: CREATE_PATIENT_VALUE, label: translate('createNewPatient'), linkOption: true }, ...options]
            : options;
    }
}
